import React, { useState, useEffect } from 'react';
import { X, FileText, Target, Package, AlertCircle, Info, ChevronRight, ChevronLeft, Check, Truck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '@/hooks/errors/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface WorkOrderSpecifications {
  problem_statement?: string;
  success_metrics?: string[];
  key_requirements?: string[];
  deliverables?: string[];
  constraints?: string[];
}

interface WorkOrderSpecificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workOrderId: string;
  workOrderName: string;
  onSave?: () => void;
}

type StepId = 'problem' | 'deliverables' | 'requirements' | 'constraints' | 'success';

interface Step {
  id: StepId;
  title: string;
  description: string;
  icon: React.ElementType;
  iconColor: string;
}

const steps: Step[] = [
  {
    id: 'problem',
    title: 'Problem',
    description: 'What problem are we solving?',
    icon: AlertCircle,
    iconColor: 'text-orange-500',
  },
  {
    id: 'deliverables',
    title: 'Deliverables',
    description: 'What will be delivered?',
    icon: Truck,
    iconColor: 'text-purple-500',
  },
  {
    id: 'requirements',
    title: 'Requirements',
    description: 'What must be included?',
    icon: Package,
    iconColor: 'text-blue-500',
  },
  {
    id: 'constraints',
    title: 'Constraints',
    description: 'Any limitations?',
    icon: Info,
    iconColor: 'text-red-500',
  },
  {
    id: 'success',
    title: 'Success',
    description: 'How will we measure success?',
    icon: Target,
    iconColor: 'text-green-500',
  },
];

export const WorkOrderSpecificationsModal: React.FC<WorkOrderSpecificationsModalProps> = ({
  isOpen,
  onClose,
  workOrderId,
  workOrderName,
  onSave,
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState<StepId>('problem');
  
  const [specifications, setSpecifications] = useState<WorkOrderSpecifications>({
    problem_statement: '',
    success_metrics: [''],
    key_requirements: [''],
    deliverables: [''],
    constraints: [''],
  });

  // Load existing specifications when modal opens
  useEffect(() => {
    if (isOpen && workOrderId) {
      loadSpecifications();
      setCurrentStep('problem'); // Reset to first step when opening
    }
  }, [isOpen, workOrderId]);

  const loadSpecifications = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('workbook_services')
        .select('specifications')
        .eq('id', workOrderId)
        .single();

      if (error) throw error;
      
      if (data?.specifications) {
        setSpecifications({
          problem_statement: data.specifications.problem_statement || '',
          success_metrics: data.specifications.success_metrics?.length > 0 
            ? data.specifications.success_metrics 
            : [''],
          key_requirements: data.specifications.key_requirements?.length > 0 
            ? data.specifications.key_requirements 
            : [''],
          deliverables: data.specifications.deliverables?.length > 0 
            ? data.specifications.deliverables 
            : [''],
          constraints: data.specifications.constraints?.length > 0 
            ? data.specifications.constraints 
            : [''],
        });
      }
    } catch (error) {
      console.error('Error loading specifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to load specifications',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleArrayFieldChange = (
    field: 'success_metrics' | 'key_requirements' | 'deliverables' | 'constraints',
    index: number,
    value: string
  ) => {
    setSpecifications(prev => ({
      ...prev,
      [field]: prev[field]?.map((item, i) => i === index ? value : item) || [],
    }));
  };

  const addArrayField = (field: 'success_metrics' | 'key_requirements' | 'deliverables' | 'constraints') => {
    setSpecifications(prev => ({
      ...prev,
      [field]: [...(prev[field] || []), ''],
    }));
  };

  const removeArrayField = (
    field: 'success_metrics' | 'key_requirements' | 'deliverables' | 'constraints',
    index: number
  ) => {
    setSpecifications(prev => ({
      ...prev,
      [field]: prev[field]?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleSave = async () => {
    // Validate that problem statement is filled
    if (!specifications.problem_statement?.trim()) {
      toast({
        title: 'Missing Information',
        description: 'Please describe the problem you\'re trying to solve',
        variant: 'destructive',
      });
      setCurrentStep('problem');
      return;
    }

    // Filter out empty strings from arrays
    const cleanedSpecs = {
      ...specifications,
      success_metrics: specifications.success_metrics?.filter(s => s.trim()) || [],
      key_requirements: specifications.key_requirements?.filter(s => s.trim()) || [],
      deliverables: specifications.deliverables?.filter(s => s.trim()) || [],
      constraints: specifications.constraints?.filter(s => s.trim()) || [],
    };

    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('workbook_services')
        .update({ specifications: cleanedSpecs })
        .eq('id', workOrderId);

      if (error) throw error;

      toast({
        title: 'Specifications Saved',
        description: 'Work order specifications have been updated',
        variant: 'default',
      });

      onSave?.();
      onClose();
    } catch (error) {
      console.error('Error saving specifications:', error);
      toast({
        title: 'Error',
        description: 'Failed to save specifications',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getCurrentStepIndex = () => steps.findIndex(s => s.id === currentStep);
  
  const isStepComplete = (stepId: StepId) => {
    switch (stepId) {
      case 'problem':
        return !!specifications.problem_statement?.trim();
      case 'deliverables':
        return specifications.deliverables?.some(s => s.trim()) || false;
      case 'requirements':
        return specifications.key_requirements?.some(s => s.trim()) || false;
      case 'constraints':
        return specifications.constraints?.some(s => s.trim()) || false;
      case 'success':
        return specifications.success_metrics?.some(s => s.trim()) || false;
      default:
        return false;
    }
  };
  
  const goToNextStep = async () => {
    // Auto-save current step data
    await saveCurrentStepData();
    
    const currentIndex = getCurrentStepIndex();
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id);
    }
  };
  
  const saveCurrentStepData = async () => {
    // Filter out empty strings from arrays
    const cleanedSpecs = {
      problem_statement: specifications.problem_statement,
      success_metrics: specifications.success_metrics?.filter(s => s.trim()) || [],
      key_requirements: specifications.key_requirements?.filter(s => s.trim()) || [],
      deliverables: specifications.deliverables?.filter(s => s.trim()) || [],
      constraints: specifications.constraints?.filter(s => s.trim()) || [],
    };

    try {
      const { error } = await supabase
        .from('workbook_services')
        .update({ specifications: cleanedSpecs })
        .eq('id', workOrderId);

      if (error) throw error;
    } catch (error) {
      console.error('Error auto-saving specifications:', error);
      // Don't show error toast for auto-save, just log it
    }
  };

  const goToPreviousStep = async () => {
    // Auto-save current step data when going back too
    await saveCurrentStepData();
    
    const currentIndex = getCurrentStepIndex();
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 'problem':
        return specifications.problem_statement?.trim() !== '';
      case 'deliverables':
        return specifications.deliverables?.some(s => s.trim()) || true; // Optional
      case 'requirements':
        return specifications.key_requirements?.some(s => s.trim()) || true; // Optional
      case 'constraints':
        return true; // Always can proceed from constraints
      case 'success':
        return specifications.success_metrics?.some(s => s.trim()) || true; // Optional
      default:
        return true;
    }
  };

  const renderStepContent = () => {
    const slideVariants = {
      enter: (direction: number) => ({
        x: direction > 0 ? 300 : -300,
        opacity: 0
      }),
      center: {
        x: 0,
        opacity: 1
      },
      exit: (direction: number) => ({
        x: direction < 0 ? 300 : -300,
        opacity: 0
      })
    };

    switch (currentStep) {
      case 'problem':
        return (
          <motion.div
            key="problem"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label className="text-base font-medium flex items-center gap-2">
                <AlertCircle className="h-4 w-4 text-orange-500" />
                Problem Statement
              </Label>
              <Textarea
                value={specifications.problem_statement}
                onChange={(e) => setSpecifications(prev => ({ ...prev, problem_statement: e.target.value }))}
                placeholder="Describe the problem or challenge that needs to be solved..."
                className="min-h-[200px]"
                autoFocus
              />
              <p className="text-sm text-gray-500">
                Be specific about the challenges you're facing and what's not working currently.
              </p>
            </div>
          </motion.div>
        );

      case 'requirements':
        return (
          <motion.div
            key="requirements"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label className="text-base font-medium flex items-center gap-2">
                <Package className="h-4 w-4 text-blue-500" />
                Key Requirements
              </Label>
              <p className="text-sm text-gray-500">What must be included in the solution?</p>
              <div className="space-y-2">
                {specifications.key_requirements?.map((req, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={req}
                      onChange={(e) => handleArrayFieldChange('key_requirements', index, e.target.value)}
                      placeholder="e.g., Mobile-responsive design"
                    />
                    {specifications.key_requirements!.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeArrayField('key_requirements', index)}
                        className="h-10 w-10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addArrayField('key_requirements')}
                className="w-full"
              >
                Add Requirement
              </Button>
            </div>
          </motion.div>
        );

      case 'deliverables':
        return (
          <motion.div
            key="deliverables"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label className="text-base font-medium flex items-center gap-2">
                <Truck className="h-4 w-4 text-purple-500" />
                Deliverables
              </Label>
              <p className="text-sm text-gray-500">What will be delivered to solve the problem?</p>
              <div className="space-y-2">
                {specifications.deliverables?.map((deliverable, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={deliverable}
                      onChange={(e) => handleArrayFieldChange('deliverables', index, e.target.value)}
                      placeholder="e.g., Redesigned homepage"
                    />
                    {specifications.deliverables!.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeArrayField('deliverables', index)}
                        className="h-10 w-10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addArrayField('deliverables')}
                className="w-full"
              >
                Add Deliverable
              </Button>
            </div>
          </motion.div>
        );

      case 'constraints':
        return (
          <motion.div
            key="constraints"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label className="text-base font-medium flex items-center gap-2">
                <Info className="h-4 w-4 text-red-500" />
                Constraints
              </Label>
              <p className="text-sm text-gray-500">Any limitations or must-haves?</p>
              <div className="space-y-2">
                {specifications.constraints?.map((constraint, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={constraint}
                      onChange={(e) => handleArrayFieldChange('constraints', index, e.target.value)}
                      placeholder="e.g., Must integrate with existing CRM"
                    />
                    {specifications.constraints!.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeArrayField('constraints', index)}
                        className="h-10 w-10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addArrayField('constraints')}
                className="w-full"
              >
                Add Constraint
              </Button>
            </div>
          </motion.div>
        );

      case 'success':
        return (
          <motion.div
            key="success"
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label className="text-base font-medium flex items-center gap-2">
                <Target className="h-4 w-4 text-green-500" />
                Success Metrics
              </Label>
              <p className="text-sm text-gray-500">How will we know when the problem is solved?</p>
              <div className="space-y-2">
                {specifications.success_metrics?.map((metric, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      value={metric}
                      onChange={(e) => handleArrayFieldChange('success_metrics', index, e.target.value)}
                      placeholder="e.g., Increase conversion rate by 25%"
                    />
                    {specifications.success_metrics!.length > 1 && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => removeArrayField('success_metrics', index)}
                        className="h-10 w-10"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => addArrayField('success_metrics')}
                className="w-full"
              >
                Add Success Metric
              </Button>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  const currentStepData = steps.find(s => s.id === currentStep);
  const currentIndex = getCurrentStepIndex();
  const isFirstStep = currentIndex === 0;
  const isLastStep = currentIndex === steps.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-500" />
            Work Order Specifications
          </DialogTitle>
          <DialogDescription>
            Define the problem and solution requirements for {workOrderName}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        <div className="px-4 py-2 overflow-x-auto">
          <div className="flex items-center justify-between min-w-fit">
            {steps.map((step, index) => {
              const Icon = step.icon;
              const isActive = step.id === currentStep;
              const isCompleted = isStepComplete(step.id);
              
              return (
                <button
                  key={step.id}
                  onClick={async () => {
                    // Save current step data before jumping to another step
                    await saveCurrentStepData();
                    setCurrentStep(step.id);
                  }}
                  className={cn(
                    "flex flex-col items-center gap-1 p-1 rounded-lg transition-all shrink-0",
                    isActive && "bg-purple-50 dark:bg-purple-900/20",
                    "hover:bg-gray-50 dark:hover:bg-gray-800/50"
                  )}
                >
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center transition-all",
                    isActive && "bg-purple-600 text-white",
                    isCompleted && !isActive && "bg-green-600 text-white",
                    !isActive && !isCompleted && "bg-gray-200 dark:bg-gray-700 text-gray-500"
                  )}>
                    {isCompleted && !isActive ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Icon className="h-4 w-4" />
                    )}
                  </div>
                  <span className={cn(
                    "text-[10px] font-medium text-center",
                    isActive && "text-purple-600 dark:text-purple-400",
                    isCompleted && !isActive && "text-green-600 dark:text-green-400",
                    !isActive && !isCompleted && "text-gray-500"
                  )}>
                    {step.title}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <div className="px-6 py-2 min-h-[300px]">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <AnimatePresence mode="wait">
              {renderStepContent()}
            </AnimatePresence>
          )}
        </div>

        <DialogFooter>
          <div className="flex items-center justify-between w-full">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            
            <div className="flex gap-2">
              {!isFirstStep && (
                <Button
                  variant="outline"
                  onClick={goToPreviousStep}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
              )}
              {!isLastStep ? (
                <Button
                  onClick={goToNextStep}
                  disabled={!canProceed()}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button
                  onClick={handleSave}
                  disabled={isSaving || !specifications.problem_statement?.trim()}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};